import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import FluidImage from "@/components/images/fluid-image";
import { CustomerPaymentMethod } from "@/networking/payments.api";
import { useTranslation } from "@/hooks/useTranslation";
import {
  resolvePaymentMethodDisplayLabel,
  resolvePaymentMethodLogoUrl,
} from "@/lib/payment-method-display";

interface ViewPaymentMethodDialogProps {
  isOpen: boolean;
  paymentMethod: CustomerPaymentMethod | null;
  onClose: () => void;
  onEdit?: () => void;
}

export default function ViewPaymentMethodDialog({
  isOpen,
  paymentMethod,
  onClose,
  onEdit,
}: ViewPaymentMethodDialogProps) {
  const { t } = useTranslation();

  if (!paymentMethod) return null;

  const { details, billing_address } = paymentMethod;
  const cardLabel = resolvePaymentMethodDisplayLabel({
    lastFour: details.last_four,
    cardBrand: details.card_brand,
    cardType: details.card_type,
    detailsType: details.type,
    paymentTitle: paymentMethod.payment_title,
    paymentType: paymentMethod.payment_type,
    source: paymentMethod.source,
  });
  const logoUrl = resolvePaymentMethodLogoUrl({
    logoUrl: details.logo_url,
    cardBrand: details.card_brand,
    cardType: details.card_type,
    detailsType: details.type,
    paymentTitle: paymentMethod.payment_title,
    paymentType: paymentMethod.payment_type,
    source: paymentMethod.source,
  });
  const logoAlt =
    details.card_brand || details.card_type || t("payment_method");
  const expMonth = details.exp_month;
  const expYear = details.exp_year;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-sm rounded md:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-md font-medium">
            {t("edit_card")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="rounded-lg bg-gray-50 p-4">
              <div className="flex items-center space-x-3">
                {logoUrl && (
                  <div className="h-6 w-10 shrink-0">
                    <FluidImage
                      src={logoUrl}
                      alt={logoAlt}
                      width={40}
                      height={24}
                      className="object-contain"
                    />
                  </div>
                )}
                <div className="flex flex-col">
                  {cardLabel && (
                    <span className="text-sm font-medium text-gray-900">
                      {cardLabel}
                    </span>
                  )}
                  {expMonth && expYear && (
                    <span className="text-sm text-gray-500">
                      {t("card_expires")} {expMonth}/{expYear}
                    </span>
                  )}
                </div>
              </div>
              {paymentMethod.default && (
                <span className="mt-2 inline-block rounded bg-gray-500 px-2 py-0.5 text-xs font-medium text-white">
                  {t("default")}
                </span>
              )}
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-900">
                {t("billing_address")}
              </h3>
              {onEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto p-0 text-sm text-gray-600 underline hover:bg-transparent"
                  onClick={onEdit}
                >
                  {t("edit")}
                </Button>
              )}
            </div>
            <div className="rounded-lg bg-gray-50 p-4">
              {billing_address ? (
                <div className="space-y-1 text-sm text-gray-900">
                  {billing_address.name && <p>{billing_address.name}</p>}
                  <p>{billing_address.address1}</p>
                  {billing_address.address2 && (
                    <p>{billing_address.address2}</p>
                  )}
                  <p>
                    {billing_address.city}, {billing_address.state}{" "}
                    {billing_address.zip}
                  </p>
                  <p>{billing_address.country_code}</p>
                  {billing_address.phone && <p>{billing_address.phone}</p>}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">
                  {t("no_billing_address")}
                </p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button
              className="rounded bg-gray-50 p-3 text-gray-900 ring-1 ring-gray-300 hover:bg-gray-100"
              onClick={onClose}
            >
              {t("close")}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
